// Klima-Card-Store. Liefert die volle Sensor-Liste; Aufruferseite
// (Dashboard-Card und /zuhause-Page) leitet den Card-Subtitle (worst-
// anomaly) sowie die Sensor-Liste pro Domain daraus ab.
//
// Datenfluss: kein eigener Abruf mehr -- die Sensoren kommen aus dem
// gemeinsamen Sensoren-Stand (lib/sensoren.svelte.ts), den das Homelab-
// Protokoll (Topic klima.bewertung, docs/protokoll.md › „Sensoren über das
// Protokoll“) speist: der Dienst rechnet die Übersicht einmal für alle Browser.
// REST nur beim Start, als Revalidator, nach Aktionen und als Neuversuch --
// dort. Die Lage (status, ok_sensors, total_sensors) zählt dieser Store aus den
// Sensoren (klimaLage; Deaktivierte zählen nicht). Bis 02.11.2026 holte er alle
// 30 s (und bei jedem WS-Signal "climate") GET /v2-api/klima/summary.
//
// SWR: ja -- die Ablage des Sensoren-Stands deckt den Reload; Tueren kommen
// ueber domain="door" aus diesem Store.

import { isEntityDisabled } from "./entity-disables.svelte";
import { klimaLage } from "./sensoren-protokoll";
import { getKlimaSensoren, startSensoren } from "./sensoren.svelte";
import type { KlimaSensor, KlimaSummary } from "./types";

export type { KlimaSensor, KlimaSummary };

let _aus: (() => void) | undefined;

export function startKlimaSummary(): void {
  if (_aus) return;
  _aus = startSensoren();
}

export function stopKlimaSummary(): void {
  _aus?.();
  _aus = undefined;
}

// Die Zusammenfassung: Sensoren aus dem Sensoren-Stand, Lage daraus gezählt.
// Das Objekt bleibt dasselbe, solange sich weder die Liste noch die Lage
// ändern -- Seiten rechnen nur neu, wenn sich etwas geändert hat. Vorhanden
// erst, wenn die Sensoren bekannt sind.
let _letzte: KlimaSummary | undefined;
const _summary = $derived.by((): KlimaSummary | undefined => {
  const sensors = getKlimaSensoren();
  if (sensors === undefined) {
    _letzte = undefined;
    return undefined;
  }
  const lage = klimaLage(sensors);
  const l = _letzte;
  if (l && l.sensors === sensors && l.status === lage.status && l.ok_sensors === lage.ok_sensors && l.total_sensors === lage.total_sensors) return l;
  _letzte = { ...lage, sensors };
  return _letzte;
});

export function getKlimaSummary(): KlimaSummary | undefined {
  return _summary;
}

// Filtert die Sensor-Liste auf Anomalien, sortiert problem vor
// warning. Genutzt von Dashboard-Card + /zuhause-Page für die
// "schärfste Anomalie zuerst"-Subtitle/Zeile.
//
// Deaktivierte Entitaeten werden rausgehalten -- sie sollen weder als
// "kritisch" auftauchen noch das Dashboard rot faerben. Wer sie in
// einer Page sehen will (Sensoren-Page "Deaktiviert"-Sektion), liest
// direkt aus s.sensors.
export function getKlimaAnomalies(s: KlimaSummary): KlimaSensor[] {
  return s.sensors
    .filter((x) => x.status !== "ok" && !x.disabled && !isEntityDisabled(x.domain, x.sensor_id))
    .sort((a, b) =>
      a.status === b.status ? 0 : a.status === "problem" ? -1 : 1,
    );
}

// Status der Summary nachdem deaktivierte Sensoren rausgenommen sind.
// Aufruferseite (Dashboard-Sensoren-Card) sollte diesen Wert nehmen
// statt summary.status, sonst bleibt das Dashboard "kritisch" obwohl
// der einzige rote Sensor deaktiviert ist.
export function getEffectiveStatus(s: KlimaSummary): "ok" | "warning" | "problem" {
  const active = s.sensors.filter(
    (x) => !x.disabled && !isEntityDisabled(x.domain, x.sensor_id),
  );
  if (active.some((x) => x.status === "problem")) return "problem";
  if (active.some((x) => x.status === "warning")) return "warning";
  return "ok";
}
